import { useContext } from 'react'

// Utils
import { getRenderStatus } from '@sketch/modules-common'

// Context
import { ShareVersionContext } from 'modules/versioning'

// Types
import { useGetPagePresentationFileQuery } from '@sketch/gql-types'
import { usePrFileStatusChangesSubscriptionIfNeeded } from './usePrFileStatusChangeSubscriptionIfNeeded'

interface UseCanvasPresentationFileProps {
  pageUUID: string
}

export const useCanvasPresentationFile = ({
  pageUUID,
}: UseCanvasPresentationFileProps) => {
  const shareVersionContext = useContext(ShareVersionContext)!
  const { versionShortId, share } = shareVersionContext

  const renderStatus = !shareVersionContext.loading
    ? getRenderStatus(
        shareVersionContext.currentVersion.document?.renderStatus ?? null
      )
    : null

  const prFileQuery = useGetPagePresentationFileQuery({
    variables: {
      shareIdentifier: share.identifier,
      versionShortId,
      pageUUID,
    },
    // Hold the PR file request until the document is marked as
    // ready on the backend.
    skip: renderStatus !== 'ready',
  })

  const page = prFileQuery.data?.share?.version?.document?.page
  const pageIdentifier = page?.identifier
  const presentationFile = page?.presentationFile

  const isLoading = shareVersionContext.loading || prFileQuery.loading
  const isRendering =
    renderStatus !== 'ready' || presentationFile?.status === 'PENDING'
  const hasError = prFileQuery.error

  // Presentation File Status Subscription
  usePrFileStatusChangesSubscriptionIfNeeded({
    renderStatus,
    presentationFile,
    pageIdentifier,
  })

  return {
    share,
    page,
    versionShortId,
    isLoading,
    isRendering,
    hasError,
  }
}
